import {useEffect, useState} from "react";
import HandleChange from "../../../../../Helpers/HandleChange";
import {CREATE_FINE_MODEL} from "../Model/Model";
import {PHOTO_PICKER_MODEL} from "../../../../../components/PhotoPicker/SubComponent/Models/Models";
import {useDispatch, useSelector} from 'react-redux';
import {USER_TYPE_MODEL} from '../../../../../enums/userTypeEnum';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {DEFAULT_LATITUDE, DEFAULT_LONGITUDE, errorMsg, getValueByKey} from '../../../../../Apis/functions';
import {
    CreateEvidencesFine,
    CreateFine,
    GetAllFineTypes
} from "../../../../../reduxToolkit/TrafficFines/TrafficFinesApi";
import {GetAllGeofenceList} from "../../../../../reduxToolkit/Traffic/TrafficApi";
import {GetGeofenceConfigSettingByGeofenceId} from "../../../../../reduxToolkit/FineGeofence/FineGeofenceApi";
import {GEOFENCES_MODEL, SPEED_UNIT_MODEL} from "../../Manage/Model/Model";

/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 *
 * @Description
 * Custom React hook that manages the logic for the **Add/Edit Fine Modal**.
 *
 * Handles:
 * - Fine form data and user selections
 * - Geofence configuration and map center
 * - File evidence uploads
 * - API integration for fine creation
 * - Validation, clear/reset, and user type filtering
 *
 * @param {Object} props - Hook properties
 * @param {boolean} props.show - Determines whether the modal is visible
 * @param {Function} props.onHide - Function to close the modal
 * @param {Function} props.setModified - Function to toggle parent component refresh state
 *
 * @returns {Object} Hook state and handler functions
 *
 * @example
 * const {
 *   item, handleChange, handleAddEdit, handleClear,
 *   photoPickerFile, setPhotoPickerFile,
 *   getGeofenceList, fineTypesAll,
 *   selectedUsers, setSelectedUsers,
 *   userTypeList, centerMap, geofence,
 *   locations, setLocations, speedUnit
 * } = AddEditFineModalApp({ show, onHide, setModified });
 */
export const AddEditFineModalApp = ({ show, onHide, setModified }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { getGeofenceList } = useSelector((state) => state.FineGeofenceSlice);
    const { fineTypesAll } = useSelector((state) => state.TrafficFinesSlice);

    /** -------------------------------
     *  Fine data and state management
     *  ------------------------------- */
    const [item, setItem] = useState({ ...CREATE_FINE_MODEL });
    const [photoPickerFile, setPhotoPickerFile] = useState({ ...PHOTO_PICKER_MODEL });
    const [emptyFields, setEmptyFields] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [userTypeList, setUserTypeList] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [centerMap, setCenterMap] = useState([DEFAULT_LATITUDE, DEFAULT_LONGITUDE]);
    const [geofence, setGeofence] = useState(null);
    const [locations, setLocations] = useState([]);
    const [speedUnit, setSpeedUnit] = useState("");

    const { handleChange } = HandleChange({ model: item, setModel: setItem });

    /**
     * Fetches geofences and fine types when the modal is shown.
     */
    useEffect(() => {
        if (show) {
            dispatch(GetAllGeofenceList());
            dispatch(GetAllFineTypes());
        }
    }, [show]);

    /**
     * Loads and maps geofence configuration when a geofence is selected.
     * Updates user type availability, speed unit, and map center.
     */
    useEffect(() => {
        if (item?.geofence !== null) {
            dispatch(GetGeofenceConfigSettingByGeofenceId(item?.geofence))
                .then(({ payload: { data: { success, data } } }) => {
                    if (success) {
                        const mapConfigToUserType = {
                            applyEmployee: USER_TYPE_MODEL.EMPLOYEE,
                            applyContractor: USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE,
                            applySupplier: USER_TYPE_MODEL.SUPPLIER_IN_CHARGE,
                        };

                        const userTypeList = Object.entries(data)
                            .filter(([key, value]) => mapConfigToUserType[key] && value === true)
                            .map(([key]) => mapConfigToUserType[key]);

                        setUserTypeList(userTypeList);
                        setSpeedUnit(getValueByKey(data?.speedUnit, 'name', SPEED_UNIT_MODEL));

                        let geofenceSelected = getGeofenceList?.find(e => e.id === item.geofence);
                        if (geofenceSelected) {
                            geofenceSelected = GEOFENCES_MODEL.toSetMap(geofenceSelected);
                            setGeofence(geofenceSelected);

                            let center = GEOFENCES_MODEL.getGeofenceCenter(geofenceSelected);
                            if (center) setCenterMap(center);
                        }

                        setLocations([]);
                    }
                });
        }
    }, [item?.geofence]);

    /**
     * Clears all modal-related state to its default values.
     *
     * @function
     * @returns {void}
     */
    const handleClear = () => {
        setCenterMap([DEFAULT_LATITUDE, DEFAULT_LONGITUDE]);
        setLocations([]);
        setGeofence(null);
        setItem({ ...CREATE_FINE_MODEL });
        setPhotoPickerFile({ ...PHOTO_PICKER_MODEL });
        setUserTypeList([]);
        setSelectedUsers([]);
    };

    /**
     * Handles the fine creation process, including validation and evidence upload.
     * Displays success or error messages accordingly.
     *
     * @async
     * @function
     * @returns {Promise<void>}
     */
    const handleAddEdit = async () => {
        if (!item.isValid(selectedUsers, locations)) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return;
        }

        setEmptyFields(false);
        const body = item.toAdd(selectedUsers[0]?.userId, locations[0]?.position[0], locations[0]?.position[1]);

        const { payload: { data: { success, data, code } } } = await dispatch(CreateFine(body));

        if (success) {
            if (photoPickerFile?.listFiles?.length === 0) {
                handleClean();
                return;
            }
            await createMultiple(data?.id || null, item?.description || "without description");
        } else {
            setModified(prev => !prev);
            handleClear();
            onHide();
            errorMsg(code);
        }
    };

    /**
     * Uploads multiple evidence files linked to a fine.
     *
     * @async
     * @function
     * @param {string} id - The fine ID to associate files with
     * @param {string} description - Description for the uploaded files
     * @returns {Promise<void>}
     */
    const createMultiple = async (id, description) => {
        if(!id || typeof id === 'undefined' || id == null){
            handleClean();
            return;
        }
        const files = photoPickerFile.listFiles;
        if (!files || files.length === 0) return;

        await Promise.all(
            files.map((file) => handleUploadFile(id, file.fileSelected, description))
        );

        handleClean();
    };

    /**
     * Handle clear before save data
     */
    const handleClean = () => {
        toast.success(t('has_been_created_successfully'));
        setModified(prev => !prev);
        handleClear();
        onHide();
    }

    /**
     * Uploads a single file as evidence for a fine.
     *
     * @async
     * @function
     * @param {string} id - Fine ID
     * @param {File} uploadFile - File object to upload
     * @param {string} description - File description
     * @returns {Promise<void>}
     */
    const handleUploadFile = (id, uploadFile, description) => {
        const formData = new FormData();
        formData.append('fileUploadDto.files', uploadFile);
        formData.append('fineId', id);
        formData.append('description', description);
        formData.append('fileUploadDto.userFileNames', uploadFile?.name || '');
        formData.append('fileUploadDto.path', '');
        return dispatch(CreateEvidencesFine(formData));
    };

    return {
        item,
        handleChange,
        handleClear,
        emptyFields,
        handleAddEdit,
        photoPickerFile,
        setPhotoPickerFile,
        getGeofenceList,
        fineTypesAll,
        disabled,
        selectedUsers, setSelectedUsers,
        userTypeList,
        centerMap, geofence,
        locations, setLocations,
        speedUnit
    };
};
